import { DeleteOutlined } from '@ant-design/icons';
import { Button, Row } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import EditableNorm from 'app/components/EditableNorm/EditableNorm';
import { StyledTable } from 'app/components/StyledTable/StyledTable';
import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toFixedIfFloat } from 'utils/helpers';
import useGetTaskResourceOptions from 'utils/hooks/useGetTaskResourceOptions';
import { findResourceById } from '../../slice/helpers';
import { ITaskUsedResource } from '../../slice/types';
import SeedCultureWarning from '../SeedCultureWarning/SeedCultureWarning';

import * as S from './TaskResourcesTable.styles';

interface IProps {
  loading: boolean;
  isEditable?: boolean;
  taskCategoryName?: string;
  area?: number;
  resourcesArr: ITaskUsedResource[];
  onDeleteClick: (resource: ITaskUsedResource) => void;
  onNormChange: (resourceId: number, norm: number) => void;
  showFieldNames?: boolean;
}

const TaskResourcesTable: FC<IProps> = ({
  loading,
  isEditable = false,
  taskCategoryName = '-',
  area = 0,
  resourcesArr: updatedUsedResourcesArr,
  showFieldNames = false,
  onDeleteClick,

  onNormChange,
}) => {
  const { t } = useTranslation();

  const [activeNormInputResourceId, setActiveNormInputResourceId] = useState<
    number | null
  >(null);

  const { allResources } = useGetTaskResourceOptions();
  const [mappedResources, setMappedResources] = useState<ITaskUsedResource[]>(
    [],
  );

  useEffect(() => {
    if (allResources.length && updatedUsedResourcesArr.length) {
      const result: ITaskUsedResource[] = updatedUsedResourcesArr.map(
        resource => ({
          ...resource,
          globalResource: findResourceById({
            resourceId: resource.resourceId,
            allResources,
          }),
        }),
      );
      setMappedResources(result);
    }
  }, [updatedUsedResourcesArr, allResources]);

  const columns: ColumnsType<ITaskUsedResource> = [
    {
      title: t('Task.ResourcesCard.Name', 'Name'),
      dataIndex: 'name',
      key: 'name',
      render: (name: string, resource: ITaskUsedResource) => {
        if (resource.cultureName && resource.cultureId) {
          return (
            <Row>
              {`${resource.cultureName} - ${name}`}
              <SeedCultureWarning seedCultureId={resource.cultureId} />
            </Row>
          );
        }
        return name;
      },
      sorter: {
        compare: (a: ITaskUsedResource, b: ITaskUsedResource) => {
          return a.name.localeCompare(b.name);
        },
      },
    },
    ...(showFieldNames
      ? [
          {
            title: t('Task.ResourcesCard.FieldName', 'Field name'),
            dataIndex: 'fieldName',
            key: 'fieldName',
            render: (fieldName?: string) => fieldName,
            width: '20%',
            sorter: {
              compare: (a: ITaskUsedResource, b: ITaskUsedResource) => {
                return a?.fieldName?.localeCompare(b?.fieldName || '') || 0;
              },
            },
          },
        ]
      : []),
    {
      title: t('Task.ResourcesCard.Category', 'Category'),
      dataIndex: 'taskCategoryName',
      key: 'category',
      render: () => taskCategoryName,
      width: '15%',
    },

    {
      title: t('Task.ResourcesCard.RecommendedNorm', 'Rec. Norm'),
      dataIndex: 'recommendedNorm',
      key: 'recommendedNorm',
      render: (
        _: unknown,
        { recommendedNorm, globalResource }: ITaskUsedResource,
      ) => `${toFixedIfFloat(recommendedNorm)} ${globalResource?.type.unit}/Ha`,
      width: '10%',
      sorter: {
        compare: (a: ITaskUsedResource, b: ITaskUsedResource) => {
          return a.recommendedNorm - b.recommendedNorm;
        },
      },
    },
    {
      title: t('Task.ResourcesCard.Norm', 'Norm'),
      dataIndex: 'norm',
      key: 'norm',
      render: (
        _: unknown,
        {
          globalResource,
          norm,
          recommendedNorm,
          resourceId,
        }: ITaskUsedResource,
      ) => (
        <>
          <EditableNorm
            activeNormInputResourceId={activeNormInputResourceId}
            norm={toFixedIfFloat(norm)}
            recommendedNorm={recommendedNorm}
            showWarningCircle={true}
            unit={globalResource?.type.unit}
            resourceId={resourceId}
            onChange={(updatedNorm: number) => {
              if (!isNaN(updatedNorm)) {
                onNormChange(resourceId, updatedNorm);
              }
            }}
            onOpen={() => setActiveNormInputResourceId(resourceId)}
            onClose={() => setActiveNormInputResourceId(null)}
            isEditable={isEditable}
          />
        </>
      ),
      width: '15%',
      sorter: {
        compare: (a: ITaskUsedResource, b: ITaskUsedResource) => {
          return a.norm - b.norm;
        },
      },
    },
    {
      title: t('Task.ResourcesCard.Area', 'Area'),
      dataIndex: 'area',
      key: 'area',
      render: (area1?: number) => `${toFixedIfFloat(area1 || area)} Ha`,
      width: '10%',
      sorter: {
        compare: (a: ITaskUsedResource, b: ITaskUsedResource) => {
          return (a?.area || area) - (b?.area || area);
        },
      },
    },
    {
      title: t('Task.ResourcesCard.Sum', 'Sum'),
      dataIndex: 'sum',
      key: 'sum',
      render: (
        _: unknown,
        { globalResource, amount, norm }: ITaskUsedResource,
      ) =>
        `${toFixedIfFloat(amount || area * norm)} ${globalResource?.type.unit}`,
      width: '10%',
      sorter: {
        compare: (a: ITaskUsedResource, b: ITaskUsedResource) => {
          const aSum = a.amount || area * a.norm;
          const bSum = b.amount || area * b.norm;

          return aSum - bSum;
        },
      },
    },
    ...(isEditable
      ? [
          {
            title: '',
            dataIndex: 'deleteResource',
            key: 'deleteResource',
            render: (_: unknown, resource: ITaskUsedResource) => (
              <Button onClick={() => onDeleteClick(resource)} type="text">
                <DeleteOutlined />
              </Button>
            ),
            width: '46px',
          },
        ]
      : []),
  ];

  return (
    <S.PlannedResourceContainer>
      <StyledTable
        loading={loading}
        bordered={true}
        columns={columns}
        rowClassName="planned-resource-row"
        dataSource={mappedResources}
        pagination={false}
        style={{ width: '100%' }}
      />
    </S.PlannedResourceContainer>
  );
};

export default TaskResourcesTable;
